import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Mail, ArrowRight, Eye, EyeOff, ShieldCheck, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Logo from '../components/Logo';
import { partnerLogin, verifyPartnerEmail, resendPartnerCode, partnerForgotPassword } from '../services/api';
import { toast } from 'sonner';

export default function PartnerLoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [verificationMode, setVerificationMode] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);

  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await partnerLogin(email, password);
      if (response.requires_verification) {
        setVerificationMode(true);
        setVerificationEmail(response.email);
        toast.info('Verification code sent to your email');
      } else {
        localStorage.setItem('adminToken', response.access_token);
        localStorage.setItem('partnerUser', JSON.stringify(response.user));
        toast.success('Login successful!');
        navigate('/partner/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setVerifying(true);
    try {
      const response = await verifyPartnerEmail(verificationEmail, verificationCode);
      localStorage.setItem('adminToken', response.access_token);
      localStorage.setItem('partnerUser', JSON.stringify(response.user));
      toast.success('Email verified! Welcome.');
      navigate('/partner/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
    } finally {
      setVerifying(false);
    }
  };

  const handleResendCode = async () => {
    setResending(true);
    try {
      await resendPartnerCode(verificationEmail);
      toast.success('New code sent to your email');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend code');
    } finally {
      setResending(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await partnerForgotPassword(forgotEmail);
      setForgotSent(true);
      toast.success('If an account exists, a reset link has been sent.');
    } catch {
      toast.error('Failed to send reset email');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0C10] flex items-center justify-center px-4" data-testid="partner-login-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="text-center mb-8 flex flex-col items-center">
          <Logo size="large" showText={false} />
          <div className="mt-4">
            <span className="text-2xl font-semibold text-[#D4AF37] heading-display">Mulberry</span>
            <span className="text-2xl font-light text-white heading-display ml-1">World</span>
          </div>
          <p className="text-[#A1A1AA] text-sm mt-2">Partner Portal</p>
        </div>

        <div className="bg-[#1A1D24] border border-[#2A2F3A] rounded-xl p-8">
          {verificationMode ? (
            <form onSubmit={handleVerify} className="space-y-6" data-testid="partner-verify-form">
              <div className="text-center mb-2">
                <ShieldCheck className="h-10 w-10 text-[#D4AF37] mx-auto mb-3" />
                <h2 className="text-white text-lg font-medium">Verify Your Email</h2>
                <p className="text-[#A1A1AA] text-sm mt-1">Enter the 6-digit code sent to <span className="text-white">{verificationEmail}</span></p>
              </div>
              <div>
                <Input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="bg-[#0B0C10] border-[#2A2F3A] text-white text-center text-2xl tracking-[0.5em] placeholder:tracking-[0.5em] placeholder:text-[#A1A1AA]/30"
                  maxLength={6}
                  required
                  data-testid="partner-verify-code-input"
                />
              </div>
              <Button type="submit" className="w-full btn-luxury" disabled={verifying || verificationCode.length !== 6} data-testid="partner-verify-btn">
                {verifying ? 'Verifying...' : 'Verify & Sign In'}
              </Button>
              <div className="flex items-center justify-between text-sm">
                <button type="button" onClick={handleResendCode} disabled={resending} className="text-[#D4AF37] hover:underline disabled:opacity-50" data-testid="partner-resend-code-btn">
                  {resending ? <><RefreshCw className="h-3 w-3 inline animate-spin mr-1" />Sending...</> : 'Resend Code'}
                </button>
                <button type="button" onClick={() => { setVerificationMode(false); setVerificationCode(''); }} className="text-[#A1A1AA] hover:text-white">
                  Back to Login
                </button>
              </div>
            </form>
          ) : forgotMode ? (
            <form onSubmit={handleForgotPassword} className="space-y-6" data-testid="partner-forgot-form">
              <div className="text-center mb-2">
                <Mail className="h-10 w-10 text-[#D4AF37] mx-auto mb-3" />
                <h2 className="text-white text-lg font-medium">Reset Password</h2>
                <p className="text-[#A1A1AA] text-sm mt-1">Enter your email to receive a reset link</p>
              </div>
              {forgotSent ? (
                <div className="text-center py-4">
                  <p className="text-green-400 text-sm mb-4">If an account exists with this email, a reset link has been sent. Check your inbox.</p>
                  <button type="button" onClick={() => { setForgotMode(false); setForgotSent(false); }} className="text-[#D4AF37] hover:underline text-sm">Back to Login</button>
                </div>
              ) : (
                <>
                  <div>
                    <Input type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="partner@company.com" className="bg-[#0B0C10] border-[#2A2F3A] text-white placeholder:text-[#A1A1AA]" required data-testid="partner-forgot-email-input" />
                  </div>
                  <Button type="submit" className="w-full btn-luxury" disabled={isLoading} data-testid="partner-forgot-btn">{isLoading ? 'Sending...' : 'Send Reset Link'}</Button>
                  <button type="button" onClick={() => setForgotMode(false)} className="w-full text-center text-[#A1A1AA] hover:text-white text-sm">Back to Login</button>
                </>
              )}
            </form>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="partner-login-form">
              <div>
                <Label htmlFor="partner-email" className="text-white mb-2 block">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#A1A1AA]" />
                  <Input id="partner-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="partner@company.com" className="pl-10 bg-[#0B0C10] border-[#2A2F3A] text-white placeholder:text-[#A1A1AA] focus:border-[#D4AF37] focus:ring-[#D4AF37]" required data-testid="partner-email-input" />
                </div>
              </div>
              <div>
                <Label htmlFor="partner-password" className="text-white mb-2 block">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#A1A1AA]" />
                  <Input id="partner-password" type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" className="pl-10 pr-12 bg-[#0B0C10] border-[#2A2F3A] text-white placeholder:text-[#A1A1AA] focus:border-[#D4AF37] focus:ring-[#D4AF37]" required data-testid="partner-password-input" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-white transition-colors">
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
              <Button type="submit" className="w-full btn-luxury" disabled={isLoading} data-testid="partner-login-btn">
                {isLoading ? 'Signing in...' : (<>Sign In <ArrowRight className="ml-2 h-5 w-5" /></>)}
              </Button>
              <button type="button" onClick={() => { setForgotMode(true); setForgotEmail(email); }} className="w-full text-center text-[#A1A1AA] hover:text-[#D4AF37] text-sm transition-colors" data-testid="partner-forgot-link">
                Forgot Password?
              </button>
            </form>
          )}
        </div>

        <div className="text-center mt-6 space-y-2">
          <p className="text-[#A1A1AA] text-sm">
            <Link to="/partner/plans" className="text-[#D4AF37] hover:underline">Become a Partner</Link>
          </p>
          <p className="text-[#A1A1AA] text-sm">
            <a href="/" className="text-[#D4AF37] hover:underline">&larr; Back to Website</a>
          </p>
        </div>
      </motion.div>
    </div>
  );
}
